import { useEffect, useState } from "react"
import { OnboardingStep } from "../onboarding-components/onboarding-step"
import { Region } from "@/dentlab/src/types/enums";
import { CountryEnum } from "@/lib/supabase/supabaseEnums";
import { RoundToType } from "@/dentlab/src/store/Jobs/utils";
import { LIECHTENSTEIN_VAT_NUMBER_FORMAT, REGION_METADATA, REGION_OPTIONS, ROUND_TO_OPTIONS } from "@/dentlab/src/types/region.types";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useAttributesStore } from "@/dentlab/src/store/Attributes";
import { ONBOARDING_STATUS, ONBOARDING_STEPS } from "@/dentlab/src/constants";
import { CustomTextFieldShadcn } from "@/dentlab/src/forms/form-input-components/custom-input-components/custom-text-field-shadcn.component";
import { CustomSelectShadcn } from "@/dentlab/src/components/custom-select-shadcn.component";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import { validateMwstNumber } from "../onboarding-validations";
import { RegionMockup } from "../onboarding-components/onboarding-mockups/RegionMockup";
import { StepProps } from "./types";

interface RegionValidationParams {
    region: Region | null;
    mwstNumber: string;
    country: string | null | undefined;
    mwstPercentage: number;
}

const validateRegion = (params: RegionValidationParams): string | null => {
    if (!params.region) return "Bitte wählen Sie eine Region aus";
    if (!validateMwstNumber(params.mwstNumber, params.country)) return "Ungültige MWST-Nummer";
    if (!params.mwstPercentage || params.mwstPercentage <= 0) return "Bitte geben Sie einen gültigen MWST-Prozentsatz ein";
    return null;
};

interface StepStandardRegionProps extends StepProps { }

export const StepStandardRegion: React.FC<StepStandardRegionProps> = ({ onComplete, stepNumber, totalSteps, canGoBack, onGoBack }) => {
    const {
        organization,
        activatedRegions,
        createOrUpdateOnboardingRegion,
    } = useCentralStore((state) => ({
        organization: state.organization,
        activatedRegions: state.activatedRegions,
        createOrUpdateOnboardingRegion: state.createOrUpdateOnboardingRegion,
    }));
    const { setOnboardingAttribute } = useAttributesStore((state) => ({
        setOnboardingAttribute: state.setOnboardingAttribute,
    }));

    const [region, setRegion] = useState<Region>(Region.CH);
    const [mwstNumber, setMwstNumber] = useState<string>("");
    const [mwstPercentage, setMwstPercentage] = useState<number>(REGION_METADATA[Region.CH].defaultVat);
    const [currencyText, setCurrencyText] = useState<string>("CHF");
    const [roundTo, setRoundTo] = useState<RoundToType>(RoundToType.TO_RAPPEN);
    const [isMwstEust, setIsMwstEust] = useState<boolean>(false);

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!organization) {
            return;
        }
        const defaultRegion = activatedRegions.find(r => r.id === organization.default_activated_region_id);
        if (defaultRegion) {
            setRegion(defaultRegion.region);
            setMwstNumber(defaultRegion.mwst_number || "");
            setMwstPercentage(defaultRegion.mwst_percentage || 8.1);
            setCurrencyText(defaultRegion.currency_text || "CHF");
            setRoundTo(defaultRegion.round_to !== null ? defaultRegion.round_to : RoundToType.TO_RAPPEN); // .round_to can be 0 so we must not use ||
            setIsMwstEust(defaultRegion.is_mwst_eust || false);
        }
    }, []);

    const handleSubmit = async () => {
        const error = validateRegion({ region, mwstNumber, mwstPercentage, country: organization?.country });
        if (error) {
            setErrorMessage(error);
            return;
        }

        setLoading(true);
        setErrorMessage(null);

        try {
            // Create/update region and save progress
            const regionId = await createOrUpdateOnboardingRegion({
                region: region,
                is_mwst_eust: isMwstEust,
                mwst_number: mwstNumber || null,
                mwst_percentage: mwstPercentage,
                currency_text: currencyText,
                round_to: roundTo,
            });

            if (regionId) {
                await setOnboardingAttribute(ONBOARDING_STEPS.REGION, ONBOARDING_STATUS.COMPLETED, 'org');
                onComplete();
            } else {
                setErrorMessage("Fehler beim Speichern der Region. Bitte versuchen Sie es erneut.");
            }
        } catch (error) {
            setErrorMessage("Fehler beim Speichern der Region. Bitte versuchen Sie es erneut.");
        } finally {
            setLoading(false);
        }
    }

    const isLiechtenstein = region == Region.CH && organization?.country === CountryEnum.LIECHTENSTEIN;
    const vatNumberFormat = isLiechtenstein
        ? LIECHTENSTEIN_VAT_NUMBER_FORMAT
        : region == Region.CH
            ? "CHE-123.456.789"
            : "DE-123.456.789";

    const handleRegionChange = (value: string | number | null) => {
        const region = value as Region;
        const regionMetadata = REGION_OPTIONS.find((r) => r.value == region);

        setRegion(region);
        setMwstPercentage(regionMetadata?.defaultVat || 8.1);
        setCurrencyText(regionMetadata?.currencyText || "CHF");
        setRoundTo(regionMetadata?.roundTo || RoundToType.TO_RAPPEN)
    };

    return (
        <OnboardingStep
            stepTitle="Standardregion konfigurieren"
            stepDescription="Diese Informationen werden für die Mehrwertsteuerberechnung, Währungsangaben und Rundung aller Preise verwendet."
            onButtonClick={handleSubmit}
            stepNumber={stepNumber}
            totalSteps={totalSteps}
            canGoBack={canGoBack}
            onGoBackClick={onGoBack}
            buttonText="Weiter"
            buttonDisabled={false}
            loading={loading}
            errorMessage={errorMessage}
            onErrorMessageClose={() => setErrorMessage(null)}
            rightPanel={<RegionMockup />}
        >
            <div className="space-y-2 sm:space-y-3">
                {/* Row 1: Region (full width) */}
                <CustomSelectShadcn
                    inputLabel="Standardregion"
                    options={REGION_OPTIONS.map((r) => ({
                        value: r.value,
                        label: r.label,
                    }))}
                    value={region}
                    onChange={handleRegionChange}
                    required
                    triggerClassName="w-full h-9 sm:h-10"
                />

                {/* Row 2: MWST-Nummer + MWST-Prozentsatz */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-start">
                    <CustomTextFieldShadcn
                        label="MWST-Nummer"
                        id="mwstNumber"
                        name="mwstNumber"
                        value={mwstNumber}
                        onChange={(e) => setMwstNumber(e.target.value)}
                        classNameInput="h-9 sm:h-10"
                        required
                        placeholder={vatNumberFormat}
                        customHelperText={`Format: ${vatNumberFormat}${isLiechtenstein ? " (5-stellig)" : ""}`}
                    />
                    <CustomTextFieldShadcn
                        label="MWST-Prozentsatz"
                        id="mwstPercentage"
                        name="mwstPercentage"
                        type="number"
                        step="0.1"
                        value={mwstPercentage}
                        onChange={(e) => setMwstPercentage(parseFloat(e.target.value))}
                        classNameInput="h-9 sm:h-10"
                        required
                    />
                </div>

                {/* Row 3: Währung + Rundung */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-start">
                    <CustomTextFieldShadcn
                        label="Währung"
                        id="currencyText"
                        name="currencyText"
                        type="text"
                        value={currencyText}
                        onChange={(e) => setCurrencyText(e.target.value)}
                        classNameInput="h-9 sm:h-10"
                        required
                    />
                    <CustomSelectShadcn
                        inputLabel="Rundung"
                        options={ROUND_TO_OPTIONS}
                        value={roundTo}
                        onChange={(value) =>
                            setRoundTo(value as RoundToType)
                        }
                        required
                        triggerClassName="w-full h-9 sm:h-10"
                    />
                </div>

                {/* Row 4: Checkbox */}
                <div className="flex items-center space-x-2">
                    <Checkbox
                        id="isMwstEust"
                        checked={isMwstEust}
                        onCheckedChange={(checked) =>
                            setIsMwstEust(checked as boolean)
                        }
                    />
                    <label
                        htmlFor="isMwstEust"
                        className="text-sm font-medium leading-none cursor-pointer"
                    >
                        Wir nutzen EUSt. statt MwSt.
                    </label>
                </div>
            </div>
        </OnboardingStep>
    )
}